import {
  ICallDetailsFormValues,
  IMeetingCallConfigurationsFormValues,
  IOaccConfigurationsFormValues,
  IQuestion,
  IWebinarConfigurationsFormValues,
  QuestionType,
} from '../interfaces';
import {
  IMeetingCallConfigurations,
  IOaccCallConfigurations,
  IWebinarCallConfigurations,
} from '@evercall/domain/vo/call/CallConfigurations';

import {
  QUESTIONS_MEETING,
  QUESTIONS_OACC,
  QUESTIONS_WEBINAR,
} from '../constants';
import { mapOptionToValue } from '@framework/data-helpers/selectOptionMappers';
import getZonedUTCDate from '@framework/date-helpers/getZonedUTCDate';
import getDateFromUnixTime from '@framework/date-helpers/getDateFromUnixTime';
import getUnixTimestamp from '@framework/date-helpers/getUnixTimestamp';

const getBinaryQuestionPayloadValue = <TBinaryQuestionAnswer>(
  fieldValue: TBinaryQuestionAnswer,
  fieldQuestion: IQuestion,
): boolean => {
  switch (fieldQuestion.type) {
    case QuestionType.CheckboxOnly:
      return (fieldValue as boolean) || false;
    case QuestionType.PseudoRadio:
      if (typeof fieldValue === 'boolean') {
        return (fieldValue as boolean);
      }
      return (fieldValue as string) ? fieldValue === 'Yes' : false;
    default:
      return null;
  }
};

const getAdjustedUnixTimestamp = (timeZone: string, unixTime: number): number => {
  const date = getDateFromUnixTime(unixTime);
  const zonedDate = getZonedUTCDate(timeZone, date);
  return getUnixTimestamp(zonedDate);
};

export const getOaccConfigurationsSubmitPayload = <T = string>(
  values: IOaccConfigurationsFormValues<T> & { timeZoneId: ICallDetailsFormValues['timeZoneId'] },
): IOaccCallConfigurations => {
  const {
    attendeeCheckInProcess,
    customScript,
    roadshowSharedPresentation,
    qaSession,
    speakerPresentingProcess,
    separatedConferenceLines,
    speakerDashboard,
    callRecording,
    transcriptionServices,
    recordedReplay,
    registrationAhead,
    roadshowSharedPresentationActivationTime,
    roadshowSharedPresentationDeactivationTime,
    timeZoneId,
  } = values;

  const isRoadshowSharedPresentation = getBinaryQuestionPayloadValue<string>(
    roadshowSharedPresentation,
    QUESTIONS_OACC.ROADSHOW_SHARED_PRESENTATION,
  );

  return {
    attendeeCheckInProcess,
    speakerPresentingProcess: speakerPresentingProcess || null,
    recordedReplay: recordedReplay || null,
    registrationAhead: getBinaryQuestionPayloadValue<T>(registrationAhead, QUESTIONS_OACC.REGISTRATION_AHEAD),
    customScript: getBinaryQuestionPayloadValue<T>(customScript, QUESTIONS_OACC.CUSTOM_SCRIPT),
    qaSession,
    speakerDashboard: getBinaryQuestionPayloadValue<string>(speakerDashboard, QUESTIONS_OACC.SPEAKER_DASHBOARD),
    callRecording: getBinaryQuestionPayloadValue<string>(callRecording, QUESTIONS_OACC.CALL_RECORDING),
    transcriptionServices: transcriptionServices || null,
    roadshowSharedPresentation: isRoadshowSharedPresentation,
    roadshowSharedPresentationActivationTime: isRoadshowSharedPresentation ?
      getAdjustedUnixTimestamp(timeZoneId.timeZone, roadshowSharedPresentationActivationTime) : null,
    roadshowSharedPresentationDeactivationTime: isRoadshowSharedPresentation ?
      getAdjustedUnixTimestamp(timeZoneId.timeZone, roadshowSharedPresentationDeactivationTime) : null,
    separatedConferenceLines: getBinaryQuestionPayloadValue<string>(
      separatedConferenceLines,
      QUESTIONS_OACC.SEPARATED_CONFERENCE_LINES,
    ),
  };
};

export const getWebinarConfigurationsSubmitPayload = (
  values: IWebinarConfigurationsFormValues,
): IWebinarCallConfigurations => {
  const {
    webinarPlatformId,
    speakers,
    speakerPresentingProcessWebinar,
    transcriptionServicesWebinar,
    webReplay,
    qaSessionWebinar,
    separatedSessions,
    callRecordingProcess,
  } = values;

  return {
    webinarPlatformId: mapOptionToValue(webinarPlatformId) as string,
    speakers,
    speakerPresentingProcess: speakerPresentingProcessWebinar || null,
    callRecordingProcess: getBinaryQuestionPayloadValue<string>(
      callRecordingProcess,
      QUESTIONS_WEBINAR.MP4,
    ),
    webReplay: getBinaryQuestionPayloadValue<string>(
      webReplay,
      QUESTIONS_WEBINAR.WEB_REPLAY,
    ),
    qaSession: qaSessionWebinar,
    transcriptionServices: transcriptionServicesWebinar || null,
    separatedSessions: getBinaryQuestionPayloadValue<string>(
      separatedSessions,
      QUESTIONS_WEBINAR.SEPARATED_SESSION_LINES,
    ),
  };
};

export const getMeetingConfigurationsSubmitPayload = <T = string>(
  values: IMeetingCallConfigurationsFormValues<T>,
): IMeetingCallConfigurations => {
  const {
    managedMeetingPlatformId,
    registrationAheadMeeting,
    moderation,
    moderationHostName,
    moderationHostEmail,
    slidesOnlyRoadshowRequired,
    slidesOnlyRoadshowAdditionalNotes,
    recording,
    transcription,
    registrationCustomization,
    registrationCustomizationCompanyLogo,
    registrationCustomizationBackgroundImage,
  } = values;

  return {
    managedMeetingPlatformId: mapOptionToValue(managedMeetingPlatformId) as string,
    registrationAhead: getBinaryQuestionPayloadValue<T>(
      registrationAheadMeeting,
      QUESTIONS_MEETING.REGISTRATION_AHEAD,
    ),
    moderation: getBinaryQuestionPayloadValue<T>(
      moderation,
      QUESTIONS_MEETING.MODERATION,
    ),
    moderationHostName: moderationHostName || '',
    moderationHostEmail: moderationHostEmail || '',
    slidesOnlyRoadshowRequired: getBinaryQuestionPayloadValue<T>(
      slidesOnlyRoadshowRequired,
      QUESTIONS_MEETING.SLIDES_ONLY_ROADSHOW_REQUIRED,
    ),
    slidesOnlyRoadshowAdditionalNotes: slidesOnlyRoadshowAdditionalNotes || '',
    recording: getBinaryQuestionPayloadValue<T>(
      recording,
      QUESTIONS_MEETING.CALL_RECORDING,
    ),
    transcription: getBinaryQuestionPayloadValue<T>(
      transcription,
      QUESTIONS_MEETING.CALL_TRANSCRIPTION,
    ),
    registrationCustomization: getBinaryQuestionPayloadValue<T>(
      registrationCustomization,
      QUESTIONS_MEETING.CUSTOMIZE_REGISTRATION,
    ),
    registrationCustomizationCompanyLogo,
    registrationCustomizationBackgroundImage,
  };
};
